import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2 } from 'lucide-react';
import { useChatStore } from '@/stores/chatStore';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function SearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { conversations, loadConversations, selectConversation, deleteConversation } = useChatStore();

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const filtered = query.trim()
    ? conversations.filter((c) =>
        (c.title ?? '').toLowerCase().includes(query.toLowerCase()) ||
        (c.lastMessage ?? '').toLowerCase().includes(query.toLowerCase())
      )
    : conversations;

  const handleSelect = async (id: string) => {
    await selectConversation(id);
    navigate('/chat');
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm('Delete this conversation?')) {
      await deleteConversation(id);
    }
  };

  return (
    <div className="flex h-full flex-col bg-background text-foreground">
      <div className="mx-auto w-full max-w-2xl px-4 py-8">
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search chats"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            className="w-full rounded-xl border border-border bg-card py-3 pl-12 pr-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {filtered.length > 0 && (
          <>
            <h2 className="mb-4 text-sm font-medium text-muted-foreground">
              {query.trim() ? 'Results' : 'Recent'}
            </h2>
            <ul className="space-y-1">
              {filtered.map((conversation) => (
                <li key={conversation.id}>
                  <button
                    onClick={() => handleSelect(conversation.id)}
                    className="group flex w-full items-center justify-between rounded-lg px-3 py-3 text-left transition-colors hover:bg-secondary"
                  >
                    <span className="truncate text-sm text-foreground">
                      {conversation.title ?? 'New conversation'}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(conversation.updatedAt)}
                      </span>
                      <button
                        onClick={(e) => handleDelete(e, conversation.id)}
                        className="hidden rounded p-1 text-muted-foreground hover:bg-destructive/20 hover:text-destructive group-hover:block"
                        aria-label="Delete conversation"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {filtered.length === 0 && query.trim() && (
          <p className="text-center text-sm text-muted-foreground py-8">
            No conversations matching &ldquo;{query}&rdquo;
          </p>
        )}

        {filtered.length === 0 && !query.trim() && (
          <p className="text-center text-sm text-muted-foreground py-8">
            No conversations yet. Start a new chat!
          </p>
        )}
      </div>
    </div>
  );
}
